import React, { useMemo, useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { MemoLoadingScreen } from '@atlassian/generative-ai-modal/screens/Loading';

import { commitPartialStreamAndCleanup } from '../../../../editor-actions/actions';
import { aiSessionContextPluginKey } from '../../../../pm-plugins/ai-session-context/ai-session-context-plugin-key';
import { clearSelectionDecorations } from '../../../../pm-plugins/decoration/actions';
import { aiExperienceDecorationPluginKey } from '../../../../pm-plugins/decoration/decoration-plugin-key';
import { useCollapseLoadingBarExposure } from '../../../hooks/useCollapseLoadingBarExposure';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { useRovoAbort } from '../../../hooks/useRovoDiscard';
import { isCollapseLoadingBarEnabledNoExposure } from '../../../utils/collapse-loading-bar-enabled';
import { Container } from '../Container';
import { useStreamingScroll } from '../hooks/useStreamingScroll';
import type { Action } from '../hooks/useUIState';
import { useUserDiscard } from '../hooks/useUserDiscard';
import type { SessionUIComponentProps } from '../SessionUIComponent';

export function LoadingSessionScreen({
	api,
	providerFactory,
	appearance,
	dispatch,
}: SessionUIComponentProps & { dispatch: React.Dispatch<Action> }): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { mounted: isRovoMounted, streaming: isRovoStreaming } = useUIStore();
	const { isInDocumentStreamingEnabled, endExperience, canInlineStreamChanges, editorView } =
		useContentComponentContext();
	const hideLoadingScreen = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(state) => state.aiExperienceState?.aiDecorationExperiencePluginState.hideLoadingScreen,
	);
	const nextSessionId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(state) => state.aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
	);

	// Per-experience override for the loading UI. Confluence's Create with Rovo
	// e.g., blank object experience sets configItem.loadingExperience = 'rovo-thinking-bar'
	// so this loading screen renders the unified RovoThinkingBar.
	const loadingExperience = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(state) =>
			state.aiExperienceState?.aiDecorationExperiencePluginState.configItem?.loadingExperience,
	);

	// Non-null while an existing remix node (e.g. a MAUI app) is being updated.
	// In that background mode the editor stays live, so suppress the click-away
	// discard — a click in the editor must not revert the update. Escape and the
	// Stop button still cancel.
	const inlineRovoStreamingTargetId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(state) =>
			state.aiExperienceState?.aiDecorationExperiencePluginState.inlineRovoStreamingTargetId,
	);

	const loadingScreenRef = React.useRef<HTMLDivElement | null>(null);
	const isDiscardingRef = useRef(false);

	const schema = useMemo(() => api?.core.sharedState.currentState()?.schema, [api]);

	const { abort } = useRovoAbort();

	/**
	 * [platform_editor_remove_pause_streaming] Stop streaming immediately without
	 * asking the discard confirmation question. Leaves any partially streamed
	 * content in the page and opens the Post Stream Review toolbar so the user
	 * can review, undo, or keep the partial changes.
	 */
	const onStop = useCallback(() => {
		// Log exposure when users click 'cancel' instead of during streaming
		expValEquals('platform_editor_remove_pause_streaming', 'isEnabled', true);
		isDiscardingRef.current = true;

		// Commit partial content and reset streaming plugin state
		// (re-enables the editor). If no adfResponse has been received yet,
		// commitPartialStreamAndCleanup is a no-op.
		if (api) {
			// Capture session ID BEFORE commitPartialStreamAndCleanup — it dispatches
			// COMPLETE which triggers handleFinalize → END_SESSION, clearing nextSessionId.
			const activeSessionId =
				api.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState
					?.nextSessionId ?? nextSessionId;

			commitPartialStreamAndCleanup({ api, editorView });

			// Always call endSession even if session id is missing/stale so the
			// action-layer abort fallback (and PSR open flow) can still run.
			api.aiExperience.actions.endSession(activeSessionId ?? '', { type: 'abort' });
		}

		// Abort Rovo orchestrator requests.
		abort();
	}, [api, abort, editorView, nextSessionId]);

	const onDiscard = useCallback(() => {
		// Log exposure when users click 'cancel' instead of during streaming
		expValEquals('platform_editor_remove_pause_streaming', 'isEnabled', true);
		isDiscardingRef.current = true;
		if (!expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			api?.aiExperience.actions.getAiExperienceActor()?.send({ type: 'enable discard' });
		}
		dispatch({ type: 'discard' });
	}, [api, dispatch]);

	useEffect(() => {
		if (!expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			return;
		}
		// turn off diff when loading screen is shown on first render
		if (api?.trackChanges?.sharedState.currentState()?.isDisplayingChanges) {
			api?.core.actions.execute(api?.trackChanges?.commands.toggleChanges);
		}

		// clear selection decorations on first render
		clearSelectionDecorations(editorView.state)(editorView.state, editorView.dispatch);

		return () => {
			if (!expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				return;
			}
			// Only transition to accept/reject when the stream completed naturally,
			// not when unmounting because the user navigated to the discard screen.
			if (isDiscardingRef.current) {
				return;
			}

			// [CCI-15904] Skip teardown when transitioning to PostStreamReview —
			// `endExperience()` would wipe the decoration the modal just installed.
			// Proper fix tracked in CCI-16978.
			const sessionContextStatus = editorView.state
				? aiSessionContextPluginKey.getState(editorView.state)?.status
				: undefined;
			if (sessionContextStatus === 'postStreamReview') {
				return;
			}

			const decorationPluginState = aiExperienceDecorationPluginKey.getState(editorView.state);
			if (
				canInlineStreamChanges() ||
				!decorationPluginState?.withAcceptReject ||
				decorationPluginState?.configItem?.streamsMarkdown
			) {
				endExperience();
				dispatch({ type: 'reset' });
			} else {
				dispatch({ type: 'show-accept-reject' });
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [api, canInlineStreamChanges, dispatch, endExperience]);

	useUserDiscard({
		onDiscard: expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true)
			? onStop
			: onDiscard,
		ref: loadingScreenRef,
		disableClickOutside: !!inlineRovoStreamingTargetId,
	});

	const isProgrammaticScrollRef = useStreamingScroll({
		api,
		view: editorView,
		modalRef: loadingScreenRef,
	});
	const shouldMinimizeOnScroll = React.useCallback(
		() => !isProgrammaticScrollRef.current,
		[isProgrammaticScrollRef],
	);

	// Scrolling this collapses the thinking bar into its pill, matching
	// Suggested Edits. Falls back to the window when the editor has no
	// overflow-scroll ancestor.
	const isLoadingBarCollapseEnabled = isCollapseLoadingBarEnabledNoExposure();
	const scrollContainer = useMemo(() => {
		if (!isLoadingBarCollapseEnabled || !editorView) {
			return undefined;
		}

		return (
			findOverflowScrollParent(editorView.dom) ||
			(typeof window !== 'undefined' ? window : undefined)
		);
	}, [editorView, isLoadingBarCollapseEnabled]);

	// Enrol once the loading bar is actually on screen, which is the point the collapse
	// treatment either applies or does not. Deliberately independent of the experiment value
	// so the control cohort is enrolled here too.
	useCollapseLoadingBarExposure(
		!!schema && !((isRovoMounted && isRovoStreaming) || hideLoadingScreen),
	);

	return (
		<Container ref={loadingScreenRef} appearance={appearance}>
			{schema && (
				<MemoLoadingScreen
					onCancel={
						expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true)
							? onStop
							: onDiscard
					}
					preview={undefined}
					title={formatMessage(aiExperienceApplicationMessages.loadingTitle)}
					providerFactory={providerFactory}
					schema={schema}
					isStreaming
					canInlineStreamChanges
					hideLoadingScreen={(isRovoMounted && isRovoStreaming) || hideLoadingScreen}
					appearance={appearance}
					isAIFC={isInDocumentStreamingEnabled}
					isLoadingBarCollapseEnabled={isLoadingBarCollapseEnabled}
					loadingExperience={loadingExperience}
					scrollContainer={scrollContainer}
					shouldMinimizeOnScroll={shouldMinimizeOnScroll}
				/>
			)}
		</Container>
	);
}
